import React, { useState } from 'react';
import styled from 'styled-components';
import { Grid, Typography, Box, Chip, CircularProgress } from '@mui/material';
import { Tabs as MuiTabs, Tab } from '@mui/material';

import Modal from '../common/Modal';
import Avatar from '../common/Avatar';
import CopyableText from '../common/CopyableText';
import { CompanyExternalUser } from '../../data/models/companyExternalUser';
import dayjs from 'dayjs';

interface ViewExternalCrewModalProps {
	open: boolean;
	handleClose: () => void;
	user?: CompanyExternalUser;
}

// Groups will be displayed from user.groups

export default function ViewExternalCrewModal(
	props: ViewExternalCrewModalProps
) {
	const { open, user, handleClose } = props;
	const [currentTab, setCurrentTab] = useState(0);

	const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
		setCurrentTab(newValue);
	};

	const renderOverviewTab = () => (
		<Container>
			{/* Avatar Section */}
			<AvatarContainer>
				<Avatar
					src={''} // Add profile picture to user model when available
					alt={`${user?.firstName} ${user?.lastName}`}
					sx={{ width: 48, height: 48 }}
				>
					{`${user?.firstName?.charAt(0) || ''}${
						user?.lastName?.charAt(0) || ''
					}`}
				</Avatar>
			</AvatarContainer>

			<Grid container spacing={2}>
				{/* First Name and Last Name side by side */}
				<Grid item xs={6} md={6}>
					<FieldContainer>
						<FieldLabel>First Name</FieldLabel>
						<FieldValue>{user?.firstName || '-'}</FieldValue>
					</FieldContainer>
				</Grid>
				<Grid item xs={6} md={6}>
					<FieldContainer>
						<FieldLabel>Last Name</FieldLabel>
						<FieldValue>{user?.lastName || '-'}</FieldValue>
					</FieldContainer>
				</Grid>

				{/* Email */}
				<Grid item xs={12} md={12}>
					<FieldContainer>
						<FieldLabel>Email</FieldLabel>
						<CopyableText text={user?.email || ''} />
					</FieldContainer>
				</Grid>

				{/* Phone Number */}
				<Grid item xs={12} md={12}>
					<FieldContainer>
						<FieldLabel>Phone Number</FieldLabel>
						<FieldValue>{user?.phone || '-'}</FieldValue>
					</FieldContainer>
				</Grid>

				{/* Role */}
				<Grid item xs={12} md={12}>
					<FieldContainer>
						<FieldLabel>Role</FieldLabel>
						<FieldValue>{user?.role || '-'}</FieldValue>
					</FieldContainer>
				</Grid>

				{/* Groups */}
				<Grid item xs={12} md={12}>
					<FieldContainer>
						<FieldLabel>Groups</FieldLabel>
						<Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
							{user?.groups && user.groups.length > 0 ? (
								user.groups.map((group) => (
									<Chip
										key={group.id}
										label={group.name}
										color="primary"
										variant="filled"
										size="small"
									/>
								))
							) : (
								<Typography
									variant="body2"
									color="text.secondary"
								>
									No groups assigned
								</Typography>
							)}
						</Box>
					</FieldContainer>
				</Grid>

				{/* Status */}
				<Grid item xs={12} md={12}>
					<FieldContainer>
						<FieldLabel>Status</FieldLabel>
						<FieldValue>{user?.status || '-'}</FieldValue>
					</FieldContainer>
				</Grid>

				{/* Created Date */}
				<Grid item xs={12} md={12}>
					<FieldContainer>
						<FieldLabel>Created</FieldLabel>
						<FieldValue>
							{user?.created
								? dayjs(user.created).format('MM/DD/YYYY')
								: '-'}
						</FieldValue>
					</FieldContainer>
				</Grid>
			</Grid>
		</Container>
	);

	const renderOnboardingInformationTab = () => (
		<Container>
			<ScrollableContent>
				{/* Basic Information Section */}
				<Section>
					<Typography variant="h6" gutterBottom>
						Basic Information
					</Typography>
					<Grid container spacing={3}>
						<Grid item xs={12} sm={6}>
							<FieldContainer>
								<FieldLabel>First Name</FieldLabel>
								<FieldValue>
									{user?.firstName || '-'}
								</FieldValue>
							</FieldContainer>
						</Grid>
						<Grid item xs={12} sm={6}>
							<FieldContainer>
								<FieldLabel>Last Name</FieldLabel>
								<FieldValue>{user?.lastName || '-'}</FieldValue>
							</FieldContainer>
						</Grid>
						<Grid item xs={12} sm={6}>
							<FieldContainer>
								<FieldLabel>Date of Birth</FieldLabel>
								<FieldValue>
									{user?.dateOfBirth
										? dayjs(user.dateOfBirth).format(
												'MM/DD/YYYY'
										  )
										: '-'}
								</FieldValue>
							</FieldContainer>
						</Grid>
						<Grid item xs={12} sm={6}>
							<FieldContainer>
								<FieldLabel>Driver License Number</FieldLabel>
								<FieldValue>
									{user?.userProfile?.driversLicenseNumber ||
										'-'}
								</FieldValue>
							</FieldContainer>
						</Grid>
						<Grid item xs={12} sm={6}>
							<FieldContainer>
								<FieldLabel>Phone Number</FieldLabel>
								<FieldValue>{user?.phone || '-'}</FieldValue>
							</FieldContainer>
						</Grid>
						<Grid item xs={12} sm={6}>
							<FieldContainer>
								<FieldLabel>Email Address</FieldLabel>
								<FieldValue>{user?.email || '-'}</FieldValue>
							</FieldContainer>
						</Grid>
						<Grid item xs={12}>
							<FieldContainer>
								<FieldLabel>Address</FieldLabel>
								<FieldValue>
									{[
										user?.userProfile?.homeAddress?.line1,
										user?.userProfile?.homeAddress?.line2,
										user?.userProfile?.homeAddress?.city,
										user?.userProfile?.homeAddress?.state,
										user?.userProfile?.homeAddress?.zip,
									]
										.filter(Boolean)
										.join(', ') || '-'}
								</FieldValue>
							</FieldContainer>
						</Grid>
						<Grid item xs={12} sm={6}>
							<FieldContainer>
								<FieldLabel>Rate</FieldLabel>
								<FieldValue>
									{user?.userProfile?.rate
										? `${user.userProfile.rate} ${
												user.userProfile.rateType ||
												'HOURLY'
										  }`
										: '-'}
								</FieldValue>
							</FieldContainer>
						</Grid>
						<Grid item xs={12}>
							<FieldContainer>
								<FieldLabel>Dietary Restrictions</FieldLabel>
								<FieldValue>
									{user?.userProfile?.dietaryRestrictions ||
										'-'}
								</FieldValue>
							</FieldContainer>
						</Grid>
					</Grid>
				</Section>

				{/* Travel Preferences Section */}
				<Section>
					<Typography variant="h6" gutterBottom>
						Travel Preferences
					</Typography>
					<Grid container spacing={3}>
						<Grid item xs={12} sm={8}>
							<Box
								sx={{
									display: 'flex',
									flexDirection: 'column',
									gap: 3,
								}}
							>
								<FieldContainer>
									<FieldLabel>Home Airport</FieldLabel>
									<FieldValue>
										{user?.userProfile?.homeAirport || '-'}
									</FieldValue>
								</FieldContainer>
								<FieldContainer>
									<FieldLabel>Passport Number</FieldLabel>
									<FieldValue>
										{user?.userProfile?.passportNumber ||
											'-'}
									</FieldValue>
								</FieldContainer>
								<FieldContainer>
									<FieldLabel>TSA PreCheck Number</FieldLabel>
									<FieldValue>
										{user?.userProfile?.tsaPrecheck || '-'}
									</FieldValue>
								</FieldContainer>
								<FieldContainer>
									<FieldLabel>AmTrack Number</FieldLabel>
									<FieldValue>
										{user?.userProfile?.amtrackNumber ||
											'-'}
									</FieldValue>
								</FieldContainer>
								<FieldContainer>
									<FieldLabel>
										Delta Mileage Number
									</FieldLabel>
									<FieldValue>
										{user?.userProfile?.deltaMileage || '-'}
									</FieldValue>
								</FieldContainer>
								<FieldContainer>
									<FieldLabel>Preferred Seating</FieldLabel>
									<FieldValue>
										{user?.userProfile?.preferredSeating ||
											'-'}
									</FieldValue>
								</FieldContainer>
								<FieldContainer>
									<FieldLabel>
										Starwood Member Number
									</FieldLabel>
									<FieldValue>
										{user?.userProfile?.starwoodMember ||
											'-'}
									</FieldValue>
								</FieldContainer>
								<FieldContainer>
									<FieldLabel>Emerald Club</FieldLabel>
									<FieldValue>
										{user?.userProfile?.emeraldClub || '-'}
									</FieldValue>
								</FieldContainer>
							</Box>
						</Grid>
						<Grid item xs={12} sm={4}>
							<PreferredAirlinerSection>
								<Typography variant="h6" gutterBottom>
									Preferred Airlines
								</Typography>
								{/* <Box
									sx={{
										display: 'flex',
										flexDirection: 'column',
										gap: 1,
									}}
								>
									{Object.entries({
										alaskan: 'Alaskan',
										american: 'American',
										delta: 'Delta',
										frontier: 'Frontier',
										klm: 'KLM/Northwest',
										southwest: 'Southwest',
										united: 'United',
										virgin: 'Virgin',
									}).map(([key, label]) => (
										<FormControlLabel
											key={key}
											control={
												<MuiCheckbox
													checked={
														user?.userProfile
															?.preferredAirlines?.[
															key as keyof typeof user.userProfile.preferredAirlines
														] || false
													}
													disabled
												/>
											}
											label={label}
										/>
									))}
								</Box> */}
							</PreferredAirlinerSection>
						</Grid>
					</Grid>
				</Section>
			</ScrollableContent>
		</Container>
	);

	if (!open || !user) return <></>;

	return (
		<Modal
			open={open}
			handleClose={handleClose}
			title="View External Person"
		>
			{user.id ? (
				<TabsContainer>
					<Box
						sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}
					>
						<MuiTabs
							value={currentTab}
							onChange={handleTabChange}
							aria-label="person tabs"
						>
							<Tab label="Overview" />
							<Tab label="Onboarding Information" />
						</MuiTabs>
					</Box>
					<TabPanel value={currentTab} index={0}>
						{renderOverviewTab()}
					</TabPanel>
					<TabPanel value={currentTab} index={1}>
						{renderOnboardingInformationTab()}
					</TabPanel>
				</TabsContainer>
			) : (
				<BusySpinnerContainer>
					<CircularProgress />
				</BusySpinnerContainer>
			)}
		</Modal>
	);
}

interface TabPanelProps {
	children?: React.ReactNode;
	index: number;
	value: number;
}

function TabPanel(props: TabPanelProps) {
	const { children, value, index, ...other } = props;

	return (
		<div
			role="tabpanel"
			hidden={value !== index}
			id={`simple-tabpanel-${index}`}
			aria-labelledby={`simple-tab-${index}`}
			style={{ height: '100%', overflow: 'auto' }}
			{...other}
		>
			{value === index && <Box sx={{ height: '100%' }}>{children}</Box>}
		</div>
	);
}

const BusySpinnerContainer = styled.div`
	padding: 50px 0;
	width: 500px;
	text-align: center;
	min-height: 150px;
`;

const Container = styled.div`
	display: flex;
	flex-direction: column;
	min-width: 500px;
	min-height: 400px;
	padding-bottom: 20px;
`;

const TabsContainer = styled.div`
	display: flex;
	flex-direction: column;
	min-width: 500px;
	min-height: 400px;
`;

const ScrollableContent = styled.div`
	flex: 1;
	overflow-y: auto;
	padding-right: 8px;
`;

const Section = styled.div`
	margin-bottom: 32px;
`;

const AvatarContainer = styled.div`
	display: flex;
	align-items: center;
	justify-content: center;
	margin-top: 24px;
	margin-bottom: 24px;
`;

const FieldContainer = styled.div`
	margin-bottom: 16px;
`;

const FieldLabel = styled(Typography)`
	color: ${({ theme }) => theme?.palette?.text?.secondary || '#666'};
	font-size: 0.875rem !important;
	font-weight: 500 !important;
	margin-bottom: 4px !important;
`;

const FieldValue = styled(Typography)`
	font-size: 1rem !important;
	color: ${({ theme }) => theme?.palette?.text?.primary || '#000'};
`;

const PreferredAirlinerSection = styled(Box)`
	padding: 1rem;
	border: 1px solid #444;
	border-radius: 8px;
`;
